import { useCallback, useEffect, useRef, useState } from 'react'

import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useIntl } from 'react-intl'

import { useDeviceInfo } from '@b-stock/fe-core/bstock-next/components'
import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import {
  Breakpoint,
  type GlobalHeaderProps,
} from '@b-stock/fe-core/bstock-react'

import SearchAutocomplete from '@/components/headerPrimaryNavigation/SearchAutocomplete'
import SearchAutosuggestion from '@/components/headerPrimaryNavigation/SearchAutosuggestion'
import { FeatureFlags } from '@/helpers/featureFlags'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import useAnimatedPlaceholder from '@/helpers/useAnimatedPlaceholder'
import { type AutocompleteAnalyticsData } from '@/helpers/useSearchAutocomplete'
import {
  useSearchDropdown,
  type AutosuggestionAnalyticsData,
} from '@/helpers/useSearchDropdown'

import { useStorefrontSearchConfig } from './useStorefrontSearchConfig'

export function useUniversalSearch(variant: 'default' | 'storefront') {
  const { shouldShowSearch, hideNavigation } =
    useStorefrontSearchConfig(variant)

  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const intl = useIntl()
  const { trackButtonClicked } = useAnalytics()

  // === SEARCH STATE ===
  // For storefront variant, don't populate search from URL query.
  const query = variant === 'storefront' ? '' : searchParams?.get('q') || ''
  const [searchValue, setSearchValue] = useState(query)
  const inputRef = useRef<HTMLInputElement>(null)

  // Update search value when URL query changes (only for default variant)
  useEffect(() => {
    if (variant !== 'storefront') {
      setSearchValue(query) // oxlint-disable-line react/set-state-in-effect
    }
  }, [query, variant])

  // === SEARCH SUBMIT HANDLER ===
  const handleOnSubmit = useCallback(
    (
      searchTerm: string,
      analyticsData?: AutocompleteAnalyticsData | AutosuggestionAnalyticsData
    ) => {
      if (searchTerm.length > 0) {
        let searchType: 'search' | 'suggestions' | 'autocomplete' = 'search'
        if (analyticsData) {
          if ('isSuggestionSelected' in analyticsData) {
            searchType = 'autocomplete'
          } else if ('search_suggestions_type' in analyticsData) {
            searchType = 'suggestions'
          }
        }

        trackButtonClicked({
          buttonName: 'search',
          search_term: searchTerm,
          search_type: searchType,
          ...(analyticsData && 'isSuggestionSelected' in analyticsData
            ? {
                is_suggested: analyticsData.isSuggestionSelected,
                user_search_input: analyticsData.autocompleteQueryValue,
                search_autocomplete: analyticsData.search_autocomplete,
              }
            : {}),
          ...(analyticsData && 'search_suggestions_type' in analyticsData
            ? {
                search_suggestions_type: analyticsData.search_suggestions_type,
                search_suggestions: analyticsData.search_suggestions,
              }
            : {}),
        })

        // Intentionally start with a clean slate — global search clears all filters
        // and page-specific params. Only the search query is carried to ALP.
        const nextParams = new URLSearchParams()
        nextParams.set('q', searchTerm)

        const queryString = nextParams.toString().replace(/\+/g, '%20')
        if (pathname?.startsWith('/all-auctions')) {
          window.history.pushState(null, '', `?${queryString}`)
        } else {
          router.push(`/all-auctions?${queryString}`, { scroll: false })
        }
      }
    },
    [pathname, router, trackButtonClicked]
  )

  // === SEARCH DROPDOWN HOOK ===
  const searchDropdown = useSearchDropdown({
    onSelectSuggestion: (suggestion, analyticsData) => {
      setSearchValue(suggestion)
      handleOnSubmit(suggestion, analyticsData)
    },
    query,
  })

  // === ANIMATED PLACEHOLDER ===
  const { activationRate, placeholders } = useFlag(
    FeatureFlags.SEARCH_BAR_ANIMATED_PLACEHOLDER_CONFIG
  )
  useAnimatedPlaceholder(inputRef, placeholders, { activationRate })

  // === ATTACH MISSING EVENT HANDLERS TO SEARCH INPUT ===
  // GlobalHeader doesn't expose onMouseDown/onKeyDown props, so attach directly.
  useEffect(() => {
    const input = inputRef.current
    if (!input) return

    const handleMouseDown = () => {
      searchDropdown.handleMouseDown()
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.key === 'Enter' &&
        (searchDropdown.isAutosuggestionOpen ||
          searchDropdown.autocomplete.isAutocompleteOpen)
      ) {
        if (
          searchDropdown.selectedAutosuggestionIndex >= 0 ||
          searchDropdown.autocomplete.selectedSuggestionIndex >= 0
        ) {
          e.preventDefault()
        }
      }

      searchDropdown.handleKeyDown(e)
    }

    input.addEventListener('mousedown', handleMouseDown)
    input.addEventListener('keydown', handleKeyDown)
    return () => {
      input.removeEventListener('mousedown', handleMouseDown)
      input.removeEventListener('keydown', handleKeyDown)
    }
  }, [searchDropdown])

  // === SEARCH HANDLERS ===
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value)
      searchDropdown.handleInputChange(value)
    },
    [searchDropdown]
  )

  const handleSearchFocus = useCallback(() => {
    searchDropdown.handleFocus()
  }, [searchDropdown])

  const handleSearchBlur = useCallback(() => {
    searchDropdown.handleBlur()
  }, [searchDropdown])

  const handleSearchClear = useCallback(() => {
    const clearParams = new URLSearchParams(searchParams?.toString())
    if (pathname?.startsWith('/all-auctions')) {
      clearParams.delete('q')
      window.history.pushState(
        null,
        '',
        `?${clearParams.toString().replace(/\+/g, '%20')}`
      )
    }
    setSearchValue('')
    searchDropdown.clearSearch()
  }, [pathname, searchParams, searchDropdown])

  const handleSearchSubmit = useCallback(
    (trimmedQuery: string) => {
      handleOnSubmit(trimmedQuery)
      searchDropdown.autocomplete.closeAutocomplete()
      searchDropdown.closeAutosuggestion()
    },
    [handleOnSubmit, searchDropdown]
  )

  // === SEARCH DROPDOWN CONTENT ===
  const searchDropdownContent = (
    <>
      {searchDropdown.activeDropdown === 'autosuggestion' && (
        <SearchAutosuggestion
          isOpen={searchDropdown.isAutosuggestionOpen}
          onSelectSuggestion={(
            suggestion: string,
            source?: 'recent' | 'trending'
          ) => {
            searchDropdown.handleSelectAutosuggestion(
              suggestion,
              source || 'recent'
            )
          }}
          onClose={() => searchDropdown.closeAutosuggestion()}
          selectedIndex={searchDropdown.selectedAutosuggestionIndex}
          inputRef={inputRef}
        />
      )}
      {searchDropdown.activeDropdown === 'autocomplete' &&
        Array.isArray(searchDropdown.autocomplete.suggestions) && (
          <SearchAutocomplete
            suggestions={searchDropdown.autocomplete.suggestions}
            isOpen={searchDropdown.autocomplete.isAutocompleteOpen}
            onSelectSuggestion={
              searchDropdown.autocomplete.handleSelectSuggestion
            }
            onClose={() => searchDropdown.autocomplete.closeAutocomplete()}
            selectedIndex={searchDropdown.autocomplete.selectedSuggestionIndex}
            searchQuery={searchValue}
            isFetching={searchDropdown.autocomplete.isFetching}
            autocompleteQueryValue={
              searchDropdown.autocomplete.autocompleteQueryValue
            }
          />
        )}
    </>
  )

  // === PLACEHOLDER TEXT ===
  const { breakpoint } = useDeviceInfo()
  const isMobile = breakpoint < Breakpoint.MEDIUM

  const searchPlaceholder = isMobile
    ? intl.formatMessage({ id: 'Search.searchBarPlaceholderMobile' })
    : intl.formatMessage({ id: 'Search.searchBarPlaceholderDesktop' })

  return {
    onSearch: shouldShowSearch ? handleSearchSubmit : undefined,
    searchPlaceholder,
    searchValue,
    onSearchChange: handleSearchChange,
    onSearchFocus: handleSearchFocus,
    onSearchBlur: handleSearchBlur,
    onSearchClear: handleSearchClear,
    searchInputRef: inputRef,
    searchDropdownContent: searchDropdownContent,
    hideNavigation,
  } satisfies Pick<
    GlobalHeaderProps,
    keyof GlobalHeaderProps &
      (`onSearch${string}` | `search${string}` | 'hideNavigation')
  >
}
